import {
  BannerConfigType,
  CheckoutPageType,
  CTAButtonPageType,
  CouponVisibilityConfigType,
  PaymentMethodConfigType,
  CouponEditorConfigType,
  CustomizeUIStateType,
  CustomizeUIPagesType,
  MarketingFlagConfigType,
  DialogDisplayConfigType
} from '../types/customizeUI'
import { DELHIVERY_CONFIRMATION_DIALOG_KEY, defaultDelhiveryDialogConfig } from 'src/assets/CustomizeUIConfig'
import {
  BannerStateConfigType,
  CheckoutUIConfigType,
  CouponVisibilityStateConfigType,
  MerchantAttributesType,
  PrimaryCTAStateConfigType
} from '../types/merchantAttributes'
import { saveDiscountOrder } from '../core/api-methods'
import { errorToast, successToast } from './toasters'
import { t } from 'i18next'
import { getRequest, putRequest } from '../core/api-client'
import { apiURI } from './constants'
import { mutate } from 'swr'
import { CustomizeUIPreviewType } from '../types/uiPreview'
import { compareStringConvertValue, createDeepCopy } from './helpers'

// Discount Issuer ID
const issuerId = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''

export const parsePaymentModesResponse = (paymentModeResponse: any): PaymentMethodConfigType[] => {
  if (!Boolean(paymentModeResponse)) return []
  const response: any = [...(paymentModeResponse as any)?.payment_methods]
  const sortableMethods = response?.filter((item: any) => Boolean(item?.weight))
  const unSortableMethods = response?.filter((item: any) => !Boolean(item?.weight))
  sortableMethods?.sort((a: any, b: any) => (a.weight > b.weight ? 1 : -1))
  const rows: PaymentMethodConfigType[] = [...sortableMethods, ...unSortableMethods]
  return rows
}

export const parseBannerConfig = (bannerConfig: BannerStateConfigType[], type: CheckoutPageType): BannerConfigType => {
  const foundConfig = bannerConfig?.find((configItem: BannerStateConfigType) => configItem?.type === type)
  return {
    color: foundConfig?.color ?? '',
    text: foundConfig?.text ?? '',
    enabled: foundConfig?.enabled ?? false,
    textColor: foundConfig?.textColor ?? ''
  }
}

export const parseCTAButtonConfig = (CTAConfig: PrimaryCTAStateConfigType[], type: CTAButtonPageType): string => {
  const foundConfig = CTAConfig?.find((configItem: PrimaryCTAStateConfigType) => configItem?.type === type)
  return foundConfig?.CTAText ?? ''
}

export const parseDiscountVisibilityConfig = (
  visibilityConfig: CouponVisibilityStateConfigType[],
  type: CheckoutPageType
): CouponVisibilityConfigType => {
  const foundConfig = visibilityConfig?.find((configItem: CouponVisibilityStateConfigType) => configItem?.type === type)
  return {
    areCouponsVisible: foundConfig?.areCouponsVisible ?? false,
    visibilityExtent: foundConfig?.visibilityExtent ?? 'cards',
    hideSavings: foundConfig?.hideSavings ?? false
  }
}

export const parseMarketingFlag = (checkoutUI?: CheckoutUIConfigType | null): MarketingFlagConfigType => {
  return {
    enableMarketingFlag: checkoutUI?.layout?.enable_marketing_flag ?? false,
    marketingConsentText: checkoutUI?.marketing_consent_text ?? 'Keep me posted about sales and offers'
  }
}
export const parseDialogDisplayConfig = (dialogDisplayConfig?: {
  dialogs?: DialogDisplayConfigType[]
}): DialogDisplayConfigType[] => {
  const dialogs = dialogDisplayConfig?.dialogs ?? []
  const hasDelhiveryDialog = dialogs.some(
    (dialog: DialogDisplayConfigType) => dialog?.key === DELHIVERY_CONFIRMATION_DIALOG_KEY
  )
  return hasDelhiveryDialog ? dialogs : [...dialogs, { ...defaultDelhiveryDialogConfig }]
}

export const getPreviewConfig = (page: CustomizeUIPagesType, state: CustomizeUIStateType): CustomizeUIPreviewType => {
  switch (page) {
    case 'BRAND':
      return {
        pageType: 'BRAND',
        config: {
          brandLogoURL: state.brandLogoURL,
          colorPalette: state.colorPalette,
          loginButtonCTAText: state.buttonCTATexts.loginButtonCTAText,
          marketingFlagConfig: state.marketingFlagConfig
        }
      }
    case 'ELEMENTS':
      return {
        pageType: 'ELEMENTS',
        config: {
          orderSummaryConfig: state.orderSummaryConfig,
          colorPalette: state.colorPalette,
          trustBadgesConfig: state.trustBadgesConfig,
          loginButtonCTAText: state.buttonCTATexts.loginButtonCTAText,
          OrderSummaryConfig: state.orderSummaryConfig
        }
      }
    case 'LOGIN':
      return {
        pageType: 'LOGIN',
        config: {
          loginBanner: state.bannerConfigs.loginBanner,
          colorPalette: state.colorPalette,
          loginButtonCTAText: state.buttonCTATexts.loginButtonCTAText,
          loginCouponVisibility: state.couponVisibilityConfig.loginCouponVisibility
        }
      }
    case 'COUPONS':
      return {
        pageType: 'COUPONS',
        config: {
          colorPalette: state.colorPalette
        }
      }
    case 'ADDRESS':
      return {
        pageType: 'ADDRESS',
        config: {
          addressBanner: state.bannerConfigs.addressBanner,
          colorPalette: state.colorPalette,
          addressButtonCTAText: state.buttonCTATexts.addressButtonCTAText,
          addressCouponVisibility: state.couponVisibilityConfig.addressCouponVisibility,
          deliveryInfoConfig: state.deliveryInfoConfig
        }
      }
    case 'PAYMENT':
      return {
        pageType: 'PAYMENT',
        config: {
          paymentBanner: state.bannerConfigs.paymentBanner,
          paymentsCouponVisibility: state.couponVisibilityConfig.paymentsCouponVisibility
        }
      }
  }
}

export const handleSaveDiscountsWeight = async (
  customizableDiscountsListData: CouponEditorConfigType[],
  accountId: string,
  refetchDiscountsData: () => void
) => {
  try {
    const sortedCouponIds = customizableDiscountsListData?.map(
      (row: CouponEditorConfigType, index: number) => row?.couponId
    )
    const payload = {
      sorted_coupon_ids: sortedCouponIds
    }
    const response: any = await saveDiscountOrder(issuerId, accountId, payload)
    if (response?.error) {
      errorToast(response?.error)
      console.error('x-trace-id:', response?.headers?.['x-trace-id'])
    } else {
      successToast(t('alert_discount_reorder_success'))
      refetchDiscountsData()
    }
  } catch (e) {
    console.error(e)
    errorToast(t('alert_discount_reorder_failed'))
  }
}

export const handleUpdatePaymentMethods = async (methods: PaymentMethodConfigType[]) => {
  if (!methods) return
  const modesPayload: any[] = []
  let currWeightCount = 1
  methods.forEach((method: any) => {
    let currWeight = method.weight
    if (currWeight) {
      currWeight = currWeightCount
      currWeightCount++
    }
    const payloadMethod = {
      mode: method.mode,
      gateway: method.gateway,
      weight: currWeight,
      metadata: {
        title: method.metadata?.title,
        description: method.metadata?.description,
        hideDescription: method.metadata?.hideDescription
      }
    }
    modesPayload.push(payloadMethod)
  })
  const payload = {
    modes: modesPayload
  }

  try {
    await putRequest(`/fms/v1/merchant-gateways/reorder-payment-modes`, apiURI.FLO_CHECKOUT, payload)
    mutate('/fms/v1/merchant-payment-modes')
    successToast('success')
  } catch (e) {
    console.error(e)
    errorToast('error')
  }
}

export const getCheckoutUIPayload = (uiState: CustomizeUIStateType): Record<string, unknown> => ({
  logo_url: uiState.brandLogoURL,
  color_pallet: {
    primary_color: uiState.colorPalette.primaryColor,
    primary_text_color: uiState.colorPalette.primaryTextColor
  },
  trust_badges: uiState.trustBadgesConfig,
  discount_bank_offers: uiState.showBankOffers,
  banner_config: [
    {
      enabled: uiState.bannerConfigs.loginBanner.enabled,
      background_color: uiState.bannerConfigs.loginBanner.color,
      html_text_string: uiState.bannerConfigs.loginBanner.text,
      type: 'AUTH',
      text_color: uiState.bannerConfigs.loginBanner.textColor
    },
    {
      enabled: uiState.bannerConfigs.addressBanner.enabled,
      background_color: uiState.bannerConfigs.addressBanner.color,
      html_text_string: uiState.bannerConfigs.addressBanner.text,
      type: 'ORDER_SUMMARY',
      text_color: uiState.bannerConfigs.addressBanner.textColor
    },
    {
      enabled: uiState.bannerConfigs.paymentBanner.enabled,
      background_color: uiState.bannerConfigs.paymentBanner.color,
      html_text_string: uiState.bannerConfigs.paymentBanner.text,
      type: 'PAYMENTS',
      text_color: uiState.bannerConfigs.paymentBanner.textColor
    }
  ],
  coupon_visibility_config: [
    {
      type: 'AUTH',
      visibile: uiState.couponVisibilityConfig.loginCouponVisibility.areCouponsVisible,
      visibility_extent: uiState.couponVisibilityConfig.loginCouponVisibility.visibilityExtent,
      hide_savings: uiState.couponVisibilityConfig.loginCouponVisibility.hideSavings
    },
    {
      type: 'ORDER_SUMMARY',
      visibile: uiState.couponVisibilityConfig.addressCouponVisibility.areCouponsVisible,
      visibility_extent: uiState.couponVisibilityConfig.addressCouponVisibility.visibilityExtent,
      hide_savings: uiState.couponVisibilityConfig.addressCouponVisibility.hideSavings
    },
    {
      type: 'PAYMENTS',
      visibile: uiState.couponVisibilityConfig.paymentsCouponVisibility.areCouponsVisible,
      visibility_extent: uiState.couponVisibilityConfig.paymentsCouponVisibility.visibilityExtent,
      hide_savings: uiState.couponVisibilityConfig.paymentsCouponVisibility.hideSavings
    }
  ],
  primary_cta_config: [
    {
      type: 'AUTH',
      cta_text: uiState.buttonCTATexts.loginButtonCTAText
    },
    {
      type: 'ORDER_SUMMARY',
      cta_text: uiState.buttonCTATexts.addressButtonCTAText
    }
  ],
  layout: {
    items_mutable: uiState.orderSummaryConfig.isQuantityEditable,
    order_summary_style: uiState.orderSummaryConfig.showOrderSummaryAs,
    enable_marketing_flag: uiState.marketingFlagConfig.enableMarketingFlag ?? false,
    metadata: {
      is_order_summary_open: uiState.orderSummaryConfig.isOrderSummaryOpen,
      show_original_price: uiState.orderSummaryConfig.isPriceStrikeThroughEnabled
    }
  },
  dialog_display_config: {
    dialogs: uiState.dialogDisplayConfig.map((dialog: DialogDisplayConfigType) =>
      dialog.key === DELHIVERY_CONFIRMATION_DIALOG_KEY
        ? { ...dialog, enabled: Boolean(dialog.enabled && uiState.orderSummaryConfig.isOnePageCheckoutEnable) }
        : dialog
    )
  },
  delivery_config: {
    delivery_text: uiState.deliveryInfoConfig.deliveryInfoText?.trim(),
    enabled: uiState.deliveryInfoConfig.isVisible,
    is_etd_enabled: uiState.deliveryInfoConfig.isETDEnabled
  },
  marketing_consent_text: uiState.marketingFlagConfig.marketingConsentText
})

export const compareCustomizeUIMerchantAttributes = (
  initialState: CustomizeUIStateType,
  compareState: CustomizeUIStateType
) => {
  const copyInitialState: Record<string, any> = createDeepCopy(initialState)
  const copyCompareState: Record<string, any> = createDeepCopy(compareState)
  delete copyInitialState.couponEditorConfig
  delete copyInitialState.paymentMethodsConfig
  delete copyCompareState.couponEditorConfig
  delete copyCompareState.paymentMethodsConfig
  return !compareStringConvertValue(copyInitialState, copyCompareState)
}

const ETD_PROVIDER_NAME_MAP: Record<string, string> = {
  SHIP_ROCKET: 'ShipRocket',
  CLICKPOST: 'ClickPost',
  ITHINK_LOGISTICS: 'iThink Logistics',
  DELHIVERY: 'Delhivery',
  SHIP_DELIGHT: 'Instalogix'
}

export const getETDProviderName = (provider: string): string => {
  if (!provider) return ''
  return ETD_PROVIDER_NAME_MAP[provider] ?? ''
}
